import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';
import { recurringApi, therapistsAvailability } from '@/api/endpoints';
import { api } from '@/api/http';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/dashboard/sessoes/recorrencia/nova')({
  // Aceita ?clientId=<id> pra pré-selecionar o cliente (atalho vindo do
  // botão "Recorrência" na lista de /dashboard/clientes).
  validateSearch: (s: Record<string, unknown>) => ({
    clientId: typeof s.clientId === 'string' ? s.clientId : undefined,
  }),
  component: NovaRecorrencia,
});

type Step = 1 | 2 | 3;
type Frequency = 'WEEKLY' | 'MONTHLY';
type PaymentMode = 'PER_SESSION' | 'UPFRONT_BATCH';
// Decisão de produto: recorrências NÃO aceitam COURTESY (cortesia é só pra
// sessões avulsas — modal em /dashboard/clientes). Aqui só PLATFORM e
// EXTERNAL_PAID. Backend ainda aceita COURTESY no enum (compatível com
// sessões avulsas), só não expomos no wizard de série.
type BillingMode = 'PLATFORM' | 'EXTERNAL_PAID';
type Modality = 'ONLINE' | 'PRESENCIAL' | 'HYBRID';
type Override = { index: number; scheduledAt: string };
type ConflictTarget = { index: number; conflictWith: string | null; originalDate: Date };

const DOW = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

// Trabalhamos com `scheduledAt` como "hora do calendário em UTC" (sem ajuste de TZ),
// alinhado com o storage `timestamp without time zone`. Por isso renderizamos e
// navegamos sempre em UTC pra evitar drift de fuso horário.
const TZ = 'UTC';

function weekLabel(d: Date) {
  const end = new Date(d.getTime() + 6 * 86400000);
  return `${d.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', timeZone: TZ })} – ${end.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', timeZone: TZ })}`;
}

function startOfWeek(date: Date): Date {
  // Midnight UTC do dia (ignora fuso local)
  return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
}

function NovaRecorrencia() {
  const navigate = useNavigate();
  const search = Route.useSearch();
  const [step, setStep] = useState<Step>(1);

  // Step 1 — se veio via ?clientId=... da lista de clientes, pré-seleciona
  const [clientId, setClientId] = useState(search.clientId ?? '');
  useEffect(() => { if (search.clientId) setClientId(search.clientId); }, [search.clientId]);
  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date()));
  const [selectedSlot, setSelectedSlot] = useState<string | null>(null);
  const [durationMin, setDurationMin] = useState(50);
  const [modality, setModality] = useState<Modality>('ONLINE');

  // Step 2
  const [frequency, setFrequency] = useState<Frequency>('WEEKLY');
  const [totalSessions, setTotalSessions] = useState(8);
  const [paymentMode, setPaymentMode] = useState<PaymentMode>('PER_SESSION');
  const [billingMode, setBillingMode] = useState<BillingMode>('PLATFORM');
  const [sessionPriceStr, setSessionPriceStr] = useState('');

  // Step 3
  const [overrides, setOverrides] = useState<Override[]>([]);
  const [conflictTarget, setConflictTarget] = useState<ConflictTarget | null>(null);

  // Success
  const [createdResult, setCreatedResult] = useState<{ scheduleId: string; paymentId: string | null } | null>(null);
  const [linkCopied, setLinkCopied] = useState(false);

  // Queries
  const { data: myClients, isLoading: clientsLoading } = useQuery({
    queryKey: ['my-clients'],
    queryFn: () =>
      api.get('invitations/my-clients').json<{
        registered: any[];
        invitations: any[];
        invClientProfileId: Record<string, string>;
      }>(),
  });

  const { data: myProfile } = useQuery({
    queryKey: ['my-profile'],
    queryFn: () => api.get('therapists/me/profile').json<any>(),
  });

  const therapistId: string = myProfile?.id ?? '';

  const availabilityQ = useQuery({
    queryKey: ['availability', therapistId, weekStart.toISOString()],
    queryFn: () => therapistsAvailability.get(therapistId, weekStart.toISOString()),
    enabled: !!therapistId,
    staleTime: 60_000,
  });

  // startTime in UTC hours (server uses setUTCHours via applyTime)
  const startDate = selectedSlot ?? '';
  const startTime = selectedSlot
    ? `${new Date(selectedSlot).getUTCHours().toString().padStart(2, '0')}:${new Date(selectedSlot).getUTCMinutes().toString().padStart(2, '0')}`
    : '10:00';
  const displayTime = selectedSlot
    ? new Date(selectedSlot).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', timeZone: TZ })
    : '';

  const previewQ = useQuery({
    queryKey: ['recurring-preview', clientId, startDate, startTime, durationMin, modality, frequency, totalSessions],
    queryFn: () =>
      recurringApi.preview({
        clientId,
        startDate: startDate || new Date().toISOString(),
        startTime,
        durationMin,
        modality,
        frequency,
        totalSessions,
      }),
    enabled: step === 3 && !!clientId && !!selectedSlot,
    staleTime: 0,
  });

  const createMut = useMutation({
    mutationFn: recurringApi.create,
    onSuccess: (data: any) => {
      const scheduleId = data?.id;
      const paymentId = data?.paymentId ?? null;
      if (paymentId && paymentMode === 'UPFRONT_BATCH') {
        setCreatedResult({ scheduleId, paymentId });
      } else {
        navigate({ to: '/dashboard/sessoes/recorrencias/$id', params: { id: scheduleId } });
      }
    },
  });

  const handleOverride = useCallback((index: number, slot: string) => {
    setOverrides((prev) => {
      const rest = prev.filter((o) => o.index !== index);
      return [...rest, { index, scheduledAt: slot }];
    });
  }, []);

  // Lista combinada de clientes (registered + invites accepted)
  const clientOptions = (() => {
    const registered = (myClients?.registered ?? []).map((c: any) => ({
      id: c.id, name: c.user?.name ?? '?',
    }));
    const accepted = (myClients?.invitations ?? [])
      .filter((inv: any) => inv.status === 'ACCEPTED' && myClients?.invClientProfileId?.[inv.id])
      .map((inv: any) => ({ id: myClients!.invClientProfileId[inv.id], name: inv.name }));
    const seen = new Set<string>();
    return [...registered, ...accepted].filter((o) => (seen.has(o.id) ? false : (seen.add(o.id), true)));
  })();

  const selectedClient = clientOptions.find((c) => c.id === clientId);
  const profileDefaultPrice = myProfile?.sessionPrice ? Number(myProfile.sessionPrice) : undefined;
  const sessionPrice = sessionPriceStr ? parseFloat(sessionPriceStr) : profileDefaultPrice;

  // O-09 — janela pra usar EXTERNAL_PAID. Se passou ou está null, opção
  // fica desabilitada com mensagem clara. COURTESY sempre disponível.
  const externalDeadline = myProfile?.externalBillingAllowedUntil
    ? new Date(myProfile.externalBillingAllowedUntil)
    : null;
  const externalDaysLeft = externalDeadline
    ? Math.ceil((externalDeadline.getTime() - Date.now()) / 86_400_000)
    : -1;
  const externalAvailable = externalDaysLeft > 0;

  const canStep1 = !!clientId && !!selectedSlot;
  const canStep2 = totalSessions >= 1 && totalSessions <= 52;

  function handleConfirm() {
    createMut.mutate({
      clientId, startDate, startTime, durationMin, modality,
      frequency, totalSessions,
      sessionPrice: sessionPrice && sessionPrice > 0 ? sessionPrice : undefined,
      paymentMode, billingMode, overrides,
    });
  }

  const conflictCount = previewQ.data?.filter((p) => p.hasConflict && !overrides.find((o) => o.index === p.index)).length ?? 0;

  const stepLabels = ['Cliente & horário', 'Frequência', 'Confirmar datas'];

  // Success screen
  if (createdResult) {
    const appUrl = typeof window !== 'undefined' ? window.location.origin : '';
    const paymentUrl = createdResult.paymentId ? `${appUrl}/pagamento/${createdResult.paymentId}` : null;
    return (
      <div className="max-w-lg space-y-6">
        <Card className="border-teal-200 bg-teal-50/40">
          <CardContent className="p-8 text-center space-y-5">
            <div className="text-5xl">🎉</div>
            <div>
              <h2 className="text-xl font-extrabold text-slate-900">Série criada!</h2>
              <p className="text-sm text-slate-500 mt-1">{totalSessions} sessões agendadas com sucesso.</p>
            </div>
            {paymentUrl && (
              <div className="space-y-3 text-left">
                <p className="text-sm font-semibold text-slate-700">Link de pagamento para o cliente:</p>
                <div className="bg-white border border-teal-200 rounded-xl px-4 py-3 flex items-center gap-3">
                  <code className="text-xs text-slate-600 break-all flex-1 select-all">{paymentUrl}</code>
                  <button
                    onClick={async () => {
                      await navigator.clipboard.writeText(paymentUrl);
                      setLinkCopied(true);
                      setTimeout(() => setLinkCopied(false), 2000);
                    }}
                    className="shrink-0 text-xs font-semibold text-teal-600 hover:text-teal-700 whitespace-nowrap"
                  >
                    {linkCopied ? 'Copiado!' : 'Copiar'}
                  </button>
                </div>
                <p className="text-xs text-slate-400">
                  Compartilhe este link com o cliente para que ele realize o pagamento antecipado das sessões.
                </p>
              </div>
            )}
            <div className="flex gap-3 pt-2">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => navigate({ to: '/dashboard/sessoes/recorrencias/$id', params: { id: createdResult.scheduleId } })}
              >
                Ver série
              </Button>
              {paymentUrl && (
                <Button
                  className="flex-1"
                  onClick={() => navigate({ to: '/pagamento/$paymentId', params: { paymentId: createdResult.paymentId! } })}
                >
                  Ver pagamento
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <>
      <div className="max-w-3xl space-y-6">
        {/* Header */}
        <div className="flex items-center gap-3">
          <button onClick={() => navigate({ to: '/dashboard/sessoes/recorrencias' })} className="text-slate-400 hover:text-slate-700 transition text-sm">←</button>
          <div>
            <h1 className="text-2xl font-extrabold text-slate-900">Nova recorrência</h1>
            <p className="text-sm text-slate-500 mt-0.5">Crie uma série de sessões para um cliente</p>
          </div>
        </div>

        {/* Step indicators */}
        <div className="flex items-center gap-2 text-sm">
          {stepLabels.map((label, i) => (
            <div key={label} className="flex items-center gap-2">
              <span className={cn(
                'inline-flex size-6 rounded-full items-center justify-center text-xs font-bold shrink-0',
                step > i + 1 ? 'bg-teal-500 text-white' : step === i + 1 ? 'bg-teal-600 text-white' : 'bg-slate-100 text-slate-400',
              )}>{i + 1}</span>
              <span className={cn('hidden sm:inline', step === i + 1 ? 'text-slate-700 font-medium' : 'text-slate-400')}>{label}</span>
              {i < 2 && <span className="text-slate-200 mx-1">—</span>}
            </div>
          ))}
        </div>

        {/* STEP 1 */}
        {step === 1 && (
          <div className="space-y-4">
            <Card>
              <CardContent className="p-5">
                <div className="grid sm:grid-cols-3 gap-4">
                  <div>
                    <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5 block">Cliente</label>
                    {clientsLoading ? (
                      <p className="text-sm text-slate-400">Carregando…</p>
                    ) : clientOptions.length === 0 ? (
                      <p className="text-sm text-slate-400">Nenhum cliente vinculado.</p>
                    ) : (
                      <select
                        value={clientId}
                        onChange={(e) => setClientId(e.target.value)}
                        className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-400 bg-white"
                      >
                        <option value="">Selecione…</option>
                        {clientOptions.map((c) => (
                          <option key={c.id} value={c.id}>{c.name}</option>
                        ))}
                      </select>
                    )}
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5 block">Duração</label>
                    <select
                      value={durationMin}
                      onChange={(e) => setDurationMin(Number(e.target.value))}
                      className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-400 bg-white"
                    >
                      {[30, 45, 50, 60, 90].map((d) => <option key={d} value={d}>{d} min</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5 block">Modalidade</label>
                    <select
                      value={modality}
                      onChange={(e) => setModality(e.target.value as Modality)}
                      className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-400 bg-white"
                    >
                      <option value="ONLINE">Online</option>
                      <option value="PRESENCIAL">Presencial</option>
                      <option value="HYBRID">Híbrido</option>
                    </select>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-5 space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="font-semibold text-slate-800">Data e horário da 1ª sessão</h2>
                    <p className="text-xs text-slate-400 mt-0.5">As sessões seguintes serão geradas a partir desta data</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={() => {
                      const d = new Date(weekStart);
                      d.setUTCDate(d.getUTCDate() - 7);
                      if (d.getTime() < Date.now() - 24 * 3600000) return;
                      setWeekStart(d); setSelectedSlot(null);
                    }}>←</Button>
                    <span className="text-xs text-slate-500 font-medium min-w-[110px] text-center">{weekLabel(weekStart)}</span>
                    <Button variant="outline" size="sm" onClick={() => {
                      const d = new Date(weekStart);
                      d.setUTCDate(d.getUTCDate() + 7);
                      setWeekStart(d); setSelectedSlot(null);
                    }}>→</Button>
                  </div>
                </div>

                {availabilityQ.isLoading && (
                  <div className="flex items-center justify-center py-12 text-sm text-slate-400">Carregando horários…</div>
                )}

                {availabilityQ.data && (
                  <div className="grid grid-cols-7 gap-1.5">
                    {availabilityQ.data.days.map((day) => {
                      const d = new Date(day.date);
                      const todayUtc = (() => { const t = new Date(); return new Date(Date.UTC(t.getFullYear(), t.getMonth(), t.getDate())); })();
                      const isPast = d.getTime() < todayUtc.getTime();
                      const isToday = d.getTime() === todayUtc.getTime();
                      return (
                        <div key={day.date} className="flex flex-col gap-1.5">
                          <div className={cn('text-center pb-2 border-b', isToday ? 'border-teal-300' : 'border-slate-100')}>
                            <div className={cn('text-[10px] font-bold uppercase tracking-wide', isToday ? 'text-teal-500' : 'text-slate-400')}>
                              {DOW[d.getUTCDay()]}
                            </div>
                            <div className={cn('text-xl font-bold leading-tight mt-0.5', isPast ? 'text-slate-300' : isToday ? 'text-teal-600' : 'text-slate-800')}>
                              {d.getUTCDate()}
                            </div>
                          </div>
                          {day.slots.length === 0 ? (
                            <div className="text-[10px] text-slate-200 text-center py-3 select-none">—</div>
                          ) : (
                            <div className="flex flex-col gap-1">
                              {day.slots.map((slot) => {
                                const time = new Date(slot).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', timeZone: TZ });
                                const isSelected = selectedSlot === slot;
                                return (
                                  <button
                                    key={slot}
                                    onClick={() => setSelectedSlot(isSelected ? null : slot)}
                                    className={cn(
                                      'text-[11px] py-1.5 rounded-lg border font-semibold transition-all',
                                      isSelected
                                        ? 'bg-teal-500 text-white border-teal-500 shadow-sm scale-[1.02]'
                                        : 'bg-white text-slate-600 border-slate-200 hover:border-teal-400 hover:text-teal-600 hover:bg-teal-50',
                                    )}
                                  >
                                    {time}
                                  </button>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                <div className={cn(
                  'flex items-center gap-2.5 rounded-xl border px-4 py-3 text-sm transition-all',
                  selectedSlot ? 'bg-teal-50 border-teal-200 text-teal-800' : 'bg-slate-50 border-slate-100 text-slate-400',
                )}>
                  {selectedSlot ? (
                    <>
                      <span className="text-teal-500 shrink-0">✓</span>
                      <span className="font-medium">
                        {new Date(selectedSlot).toLocaleString('pt-BR', { weekday: 'long', day: '2-digit', month: 'long', timeZone: TZ })}
                        {' às '}{displayTime}
                      </span>
                      <span className="ml-auto text-xs text-teal-600 font-semibold">{durationMin} min</span>
                    </>
                  ) : (
                    <span>Selecione um horário no calendário acima</span>
                  )}
                </div>
              </CardContent>
            </Card>

            <div className="flex justify-end">
              <Button onClick={() => setStep(2)} disabled={!canStep1} size="lg">Próximo →</Button>
            </div>
          </div>
        )}

        {/* STEP 2 */}
        {step === 2 && (
          <Card>
            <CardContent className="p-6 space-y-6">
              <div>
                <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2 block">Frequência</label>
                <div className="grid grid-cols-2 gap-3">
                  {(['WEEKLY', 'MONTHLY'] as Frequency[]).map((f) => (
                    <button key={f} onClick={() => setFrequency(f)} className={cn(
                      'border rounded-xl p-4 text-left transition',
                      frequency === f ? 'border-teal-400 bg-teal-50 shadow-sm' : 'border-slate-200 hover:border-slate-300',
                    )}>
                      <div className="font-semibold text-slate-800 text-sm">{f === 'WEEKLY' ? 'Semanal' : 'Mensal'}</div>
                      <div className="text-xs text-slate-400 mt-1">{f === 'WEEKLY' ? 'Mesma hora toda semana' : 'Mesma hora todo mês'}</div>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Número de sessões</label>
                  <span className="text-2xl font-extrabold text-teal-700 tabular-nums">{totalSessions}</span>
                </div>
                <input
                  type="range" min={1} max={52} value={totalSessions}
                  onChange={(e) => setTotalSessions(Number(e.target.value))}
                  className="w-full accent-teal-500"
                />
                <p className="text-xs text-slate-400 mt-1.5">
                  {frequency === 'WEEKLY'
                    ? `${totalSessions} semana${totalSessions > 1 ? 's' : ''} (≈${Math.round(totalSessions / 4)} ${Math.round(totalSessions / 4) === 1 ? 'mês' : 'meses'})`
                    : `${totalSessions} ${totalSessions === 1 ? 'mês' : 'meses'} (≈${(totalSessions / 12).toFixed(1)} ano${totalSessions >= 12 ? 's' : ''})`}
                </p>
              </div>

              <div>
                <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2 block">Valor por sessão</label>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-slate-500 font-medium">R$</span>
                  <input
                    type="number"
                    min={0}
                    step={0.01}
                    value={sessionPriceStr}
                    onChange={(e) => setSessionPriceStr(e.target.value)}
                    placeholder={profileDefaultPrice ? String(profileDefaultPrice) : '0,00'}
                    className="w-32 border border-slate-200 rounded-lg px-3 py-1.5 text-sm text-center focus:outline-none focus:ring-2 focus:ring-teal-400"
                  />
                  {profileDefaultPrice && sessionPrice && sessionPrice > 0 && sessionPrice !== profileDefaultPrice && (
                    <span className="text-xs text-teal-600 font-medium">valor promocional</span>
                  )}
                </div>
              </div>

              {/* O-01..O-09 — Modo de cobrança da série inteira.
                  Recorrência aceita só PLATFORM e EXTERNAL_PAID; COURTESY
                  fica reservada pra sessões avulsas (modal em /clientes). */}
              <div>
                <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2 block">Origem do pagamento</label>
                <div className="grid sm:grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setBillingMode('PLATFORM')}
                    className={cn(
                      'border rounded-xl p-4 text-left transition',
                      billingMode === 'PLATFORM' ? 'border-teal-400 bg-teal-50 shadow-sm' : 'border-slate-200 hover:border-slate-300',
                    )}
                  >
                    <div className="font-semibold text-slate-800 text-sm">Cobrar via Nosso Ello</div>
                    <div className="text-xs text-slate-400 mt-1">Cobrança pela plataforma (cartão ou PIX). Taxa do seu plano se aplica.</div>
                  </button>
                  <button
                    type="button"
                    disabled={!externalAvailable}
                    onClick={() => externalAvailable && setBillingMode('EXTERNAL_PAID')}
                    className={cn(
                      'border rounded-xl p-4 text-left transition',
                      !externalAvailable
                        ? 'border-slate-100 bg-slate-50 opacity-60 cursor-not-allowed'
                        : billingMode === 'EXTERNAL_PAID'
                          ? 'border-amber-400 bg-amber-50 shadow-sm'
                          : 'border-slate-200 hover:border-slate-300',
                    )}
                  >
                    <div className="font-semibold text-slate-800 text-sm">Já paga fora da plataforma</div>
                    <div className="text-xs text-slate-400 mt-1">
                      {externalAvailable
                        ? `${externalDaysLeft} dia${externalDaysLeft === 1 ? '' : 's'} restante${externalDaysLeft === 1 ? '' : 's'} pra importar carteira já paga.`
                        : 'Janela expirou. Use "Cobrar via Nosso Ello".'}
                    </div>
                  </button>
                </div>
                {billingMode !== 'PLATFORM' && (
                  <p className="text-xs text-slate-500 mt-2">
                    Sessões da série não vão gerar pagamento na plataforma nem entrar em ciclo de faturamento. Pra sessões gratuitas (cortesia), use o agendamento avulso em <strong>Clientes → Agendar sessão</strong>.
                  </p>
                )}
              </div>

              <div>
                <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2 block">Modo de pagamento</label>
                <div className="grid grid-cols-2 gap-3">
                  <button onClick={() => setPaymentMode('PER_SESSION')} className={cn(
                    'border rounded-xl p-4 text-left transition',
                    paymentMode === 'PER_SESSION' ? 'border-teal-400 bg-teal-50 shadow-sm' : 'border-slate-200 hover:border-slate-300',
                  )}>
                    <div className="font-semibold text-slate-800 text-sm">Por sessão</div>
                    <div className="text-xs text-slate-400 mt-1">Cliente paga individualmente conforme as sessões chegam</div>
                  </button>
                  {(() => {
                    const total = sessionPrice && sessionPrice > 0 && totalSessions ? sessionPrice * totalSessions : null;
                    const noPrice = !sessionPrice || sessionPrice <= 0;
                    return (
                      <button
                        onClick={() => !noPrice && setPaymentMode('UPFRONT_BATCH')}
                        disabled={noPrice}
                        className={cn(
                          'border rounded-xl p-4 text-left transition',
                          noPrice
                            ? 'border-slate-100 bg-slate-50 opacity-60 cursor-not-allowed'
                            : paymentMode === 'UPFRONT_BATCH'
                              ? 'border-teal-400 bg-teal-50 shadow-sm'
                              : 'border-slate-200 hover:border-slate-300',
                        )}
                      >
                        <div className="font-semibold text-slate-800 text-sm">Pré-pago em lote</div>
                        {noPrice
                          ? <div className="text-xs text-amber-500 mt-1">Informe o valor por sessão para usar esta opção</div>
                          : <div className="text-xs text-slate-400 mt-1">
                              Cliente paga todas as sessões de uma vez
                              {total && ` · ${total.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })} total`}
                            </div>
                        }
                      </button>
                    );
                  })()}
                </div>
              </div>

              <div className="flex justify-between pt-2 border-t border-slate-100">
                <Button variant="outline" onClick={() => setStep(1)}>← Voltar</Button>
                <Button onClick={() => setStep(3)} disabled={!canStep2}>Ver datas →</Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* STEP 3 */}
        {step === 3 && (
          <Card>
            <CardContent className="p-6 space-y-4">
              <div className="flex items-start justify-between">
                <div>
                  <h2 className="font-bold text-slate-800">Prévia das datas</h2>
                  <p className="text-xs text-slate-400 mt-0.5">
                    {selectedClient?.name} · {frequency === 'WEEKLY' ? 'Semanal' : 'Mensal'} · {totalSessions} sessões
                  </p>
                </div>
                {conflictCount > 0 && (
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-100 text-amber-700 text-xs font-semibold px-3 py-1 shrink-0">
                    ⚠ {conflictCount} conflito{conflictCount > 1 ? 's' : ''}
                  </span>
                )}
              </div>

              {previewQ.isLoading && (
                <p className="text-sm text-slate-400 text-center py-8">Verificando datas…</p>
              )}

              {previewQ.data && (
                <div className="space-y-2 max-h-96 overflow-y-auto pr-1">
                  {previewQ.data.map((item) => {
                    const override = overrides.find((o) => o.index === item.index);
                    const displayDate = override ? new Date(override.scheduledAt) : new Date(item.scheduledAt);
                    const isConflict = item.hasConflict && !override;
                    const overrideTime = override
                      ? new Date(override.scheduledAt).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', timeZone: TZ })
                      : null;

                    return (
                      <div key={item.index} className={cn(
                        'flex items-center justify-between rounded-lg border px-4 py-3 text-sm gap-3',
                        isConflict ? 'border-amber-300 bg-amber-50' : override ? 'border-teal-200 bg-teal-50/60' : 'border-slate-100 bg-white',
                      )}>
                        <div className="flex items-center gap-3 min-w-0">
                          <span className={cn(
                            'size-6 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0',
                            isConflict ? 'bg-amber-200 text-amber-700' : override ? 'bg-teal-500 text-white' : 'bg-teal-100 text-teal-700',
                          )}>
                            {item.index + 1}
                          </span>
                          <div className="min-w-0">
                            <span className={cn('font-medium', isConflict ? 'text-amber-800 line-through decoration-amber-400' : override ? 'text-slate-500' : 'text-slate-700')}>
                              {new Date(item.scheduledAt).toLocaleDateString('pt-BR', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric', timeZone: TZ })}
                              {' · '}{displayTime}
                            </span>
                            {isConflict && (
                              <p className="text-amber-600 text-xs mt-0.5">conflito com {item.conflictWith}</p>
                            )}
                            {override && (
                              <p className="text-teal-700 text-xs font-semibold mt-0.5">
                                ✓ {displayDate.toLocaleDateString('pt-BR', { weekday: 'short', day: '2-digit', month: 'short', timeZone: TZ })}
                                {' às '}{overrideTime}
                              </p>
                            )}
                          </div>
                        </div>

                        {(isConflict || override) && (
                          <button
                            onClick={() => setConflictTarget({
                              index: item.index,
                              conflictWith: item.conflictWith,
                              originalDate: new Date(item.scheduledAt),
                            })}
                            className={cn(
                              'shrink-0 text-xs font-semibold px-3 py-1.5 rounded-lg border transition whitespace-nowrap',
                              isConflict
                                ? 'border-amber-400 text-amber-700 bg-white hover:bg-amber-50'
                                : 'border-teal-300 text-teal-700 bg-white hover:bg-teal-50',
                            )}
                          >
                            {override ? 'Alterar' : 'Reagendar'}
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              <div className="flex justify-between pt-2 border-t border-slate-100">
                <Button variant="outline" onClick={() => setStep(2)}>← Voltar</Button>
                <Button onClick={handleConfirm} disabled={createMut.isPending || previewQ.isLoading}>
                  {createMut.isPending ? 'Criando…' : `Criar ${totalSessions} sessões →`}
                </Button>
              </div>

              {createMut.isError && (
                <p className="text-sm text-red-500 text-center">
                  Não foi possível criar a série. Verifique os campos e tente novamente.
                </p>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      {/* Conflict resolver modal */}
      {conflictTarget && therapistId && (
        <ResolveModal
          conflict={conflictTarget}
          therapistId={therapistId}
          onConfirm={handleOverride}
          onClose={() => setConflictTarget(null)}
        />
      )}
    </>
  );
}

function ResolveModal({
  conflict, therapistId, onConfirm, onClose,
}: {
  conflict: ConflictTarget;
  therapistId: string;
  onConfirm: (index: number, slot: string) => void;
  onClose: () => void;
}) {
  const [modalWeek, setModalWeek] = useState<Date>(() => startOfWeek(conflict.originalDate));
  const [modalSlot, setModalSlot] = useState<string | null>(null);

  const availQ = useQuery({
    queryKey: ['availability', therapistId, modalWeek.toISOString()],
    queryFn: () => therapistsAvailability.get(therapistId, modalWeek.toISOString()),
    enabled: !!therapistId,
    staleTime: 30_000,
  });

  const modalDisplayTime = modalSlot
    ? new Date(modalSlot).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', timeZone: TZ })
    : null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />

      <div className="relative z-10 w-full max-w-2xl bg-white rounded-2xl shadow-2xl flex flex-col max-h-[90vh]">
        <div className="flex items-start justify-between px-6 py-5 border-b border-slate-100">
          <div>
            <h2 className="font-bold text-slate-900 text-lg">Escolher novo horário</h2>
            <p className="text-sm text-slate-500 mt-0.5">
              Sessão #{conflict.index + 1}
              {conflict.conflictWith && (
                <span className="ml-2 text-amber-600">· conflito com {conflict.conflictWith}</span>
              )}
            </p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700 transition text-xl leading-none mt-0.5">×</button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
          <div className="flex items-center justify-between">
            <Button variant="outline" size="sm" onClick={() => {
              const d = new Date(modalWeek);
              d.setUTCDate(d.getUTCDate() - 7);
              if (d.getTime() < Date.now() - 24 * 3600000) return;
              setModalWeek(d); setModalSlot(null);
            }}>←</Button>
            <span className="text-sm text-slate-600 font-medium">{weekLabel(modalWeek)}</span>
            <Button variant="outline" size="sm" onClick={() => {
              const d = new Date(modalWeek);
              d.setUTCDate(d.getUTCDate() + 7);
              setModalWeek(d); setModalSlot(null);
            }}>→</Button>
          </div>

          {availQ.isLoading && (
            <div className="flex items-center justify-center py-12 text-sm text-slate-400">Carregando horários…</div>
          )}

          {availQ.data && (
            <div className="grid grid-cols-7 gap-1.5">
              {availQ.data.days.map((day) => {
                const d = new Date(day.date);
                const todayUtc = (() => { const t = new Date(); return new Date(Date.UTC(t.getFullYear(), t.getMonth(), t.getDate())); })();
                const isPast = d.getTime() < todayUtc.getTime();
                const isToday = d.getTime() === todayUtc.getTime();
                return (
                  <div key={day.date} className="flex flex-col gap-1.5">
                    <div className={cn('text-center pb-2 border-b', isToday ? 'border-teal-300' : 'border-slate-100')}>
                      <div className={cn('text-[10px] font-bold uppercase tracking-wide', isToday ? 'text-teal-500' : 'text-slate-400')}>
                        {DOW[d.getUTCDay()]}
                      </div>
                      <div className={cn('text-xl font-bold leading-tight mt-0.5', isPast ? 'text-slate-300' : isToday ? 'text-teal-600' : 'text-slate-800')}>
                        {d.getUTCDate()}
                      </div>
                    </div>
                    {day.slots.length === 0 ? (
                      <div className="text-[10px] text-slate-200 text-center py-3 select-none">—</div>
                    ) : (
                      <div className="flex flex-col gap-1">
                        {day.slots.map((slot) => {
                          const time = new Date(slot).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', timeZone: TZ });
                          const isSelected = modalSlot === slot;
                          return (
                            <button
                              key={slot}
                              onClick={() => setModalSlot(isSelected ? null : slot)}
                              className={cn(
                                'text-[11px] py-1.5 rounded-lg border font-semibold transition-all',
                                isSelected
                                  ? 'bg-teal-500 text-white border-teal-500 shadow-sm scale-[1.02]'
                                  : 'bg-white text-slate-600 border-slate-200 hover:border-teal-400 hover:text-teal-600 hover:bg-teal-50',
                              )}
                            >
                              {time}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          <div className={cn(
            'flex items-center gap-2.5 rounded-xl border px-4 py-3 text-sm transition-all',
            modalSlot ? 'bg-teal-50 border-teal-200 text-teal-800' : 'bg-slate-50 border-slate-100 text-slate-400',
          )}>
            {modalSlot ? (
              <>
                <span className="text-teal-500 shrink-0">✓</span>
                <span className="font-medium">
                  {new Date(modalSlot).toLocaleDateString('pt-BR', { weekday: 'long', day: '2-digit', month: 'long', timeZone: TZ })}
                  {' às '}{modalDisplayTime}
                </span>
              </>
            ) : (
              <span>Selecione um horário disponível acima</span>
            )}
          </div>
        </div>

        <div className="flex justify-between px-6 py-4 border-t border-slate-100 gap-3">
          <Button variant="outline" onClick={onClose} className="flex-1">Cancelar</Button>
          <Button
            disabled={!modalSlot}
            onClick={() => { if (modalSlot) { onConfirm(conflict.index, modalSlot); onClose(); } }}
            className="flex-1"
          >
            Confirmar horário
          </Button>
        </div>
      </div>
    </div>
  );
}
